import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import useLocalStorage from 'react-localstorage-hook';
import { isEmpty } from 'lodash-es';
import { FlexBox } from '../common/FlexBox';
import { MonacoEditor } from '../monaco-editor';

// Sample Claim payloads matching the schema
const predefinedClaimUpdatedPayloads: Record<string, any> = {
  'Sample Claim Updated A': {
    identifiers: { ehrClaimId: 'claimA', encounterId: 'encA' },
    basicInformation: {
      serviceDate: '2024-05-01',
      claimDate: '2024-05-03',
      claimStatus: 'Submitted',
      insuranceName: 'Acme Insurance',
      placeOfService: { code: '11', description: 'Office' },
    },
    renderingProvider: {
      ehrProviderId: 'provA',
      demographics: { firstName: 'John', lastName: 'Doe' },
    },
    serviceLines: [],
    additionalDiagnoses: [],
  },
};

export const ClaimUpdatedTab = () => {
  const [payload, setPayload] = useState<any>();
  const [predefinedPayload, setPredefinedPayload] = useState<string>('');
  const [lastUsedPayload, setLastUsedPayload] = useLocalStorage('lastClaimUpdatedPayload', '');
  const [, setShouldPublish] = useLocalStorage('shouldPublishClaimUpdated', false);

  useEffect(() => {
    handlePredefinedPayloadChange({ target: { value: lastUsedPayload } } as SelectChangeEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setShouldPublish(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const handleButtonClick = useCallback(
    (e) => {
      e.stopPropagation();
      setShouldPublish(true);
    },
    [setShouldPublish],
  );

  const handlePredefinedPayloadChange = useCallback(
    ({ target: { value } }: SelectChangeEvent) => {
      setPredefinedPayload(value);
      setLastUsedPayload(value);
      setPayload(predefinedClaimUpdatedPayloads[value]);
    },
    [setLastUsedPayload],
  );

  return (
    <Accordion id="claimUpdatedTab">
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>claimUpdated</Typography>
        <Select
          displayEmpty
          value={predefinedPayload}
          id="claim-updated-select"
          size="small"
          onChange={handlePredefinedPayloadChange}
          onClick={handleSelectClick}
        >
          <MenuItem value="">Choose</MenuItem>
          {Object.keys(predefinedClaimUpdatedPayloads).map((value) => (
            <MenuItem value={value} key={value} id={`claim-updated-${value.replaceAll(' ', '-')}`}>
              {value}
            </MenuItem>
          ))}
        </Select>
        <Button
          id="publish-claim-updated"
          disabled={isEmpty(predefinedPayload)}
          fullWidth
          onClick={handleButtonClick}
          variant="contained"
        >
          Publish
        </Button>
      </AccordionSummary>
      <AccordionDetails>
        <MonacoEditor onChange={setPayload} height="20rem" value={payload} />
        <FlexBox>
          <TextField
            id="claimUpdatedPayload"
            variant="outlined"
            size="small"
            value={JSON.stringify(payload)}
            sx={{ display: 'none' }}
          />
        </FlexBox>
      </AccordionDetails>
    </Accordion>
  );
};
